import React from 'react';
import { UserValidators } from '@portal/portal-core';
import * as Utils from 'utils';

interface EmailValidator {
  validator: (value: string) => boolean;
  getValidationMessage: (value: string) => React.ReactElement;
}

const getAtSignPosition = (value: string): number => value.indexOf('@');
const getPostAtSign = (value: string): string => value.substring(getAtSignPosition(value) + 1);
const getTldPeriodPosition = (value: string): number => getPostAtSign(value).lastIndexOf('.');
const getNumCharsTld = (value: string): number => {
  const postAtSign = getPostAtSign(value);
  return postAtSign.length - getTldPeriodPosition(value) - 1;
};
const getDomain = (value: string): string => {
  const postAtSign = getPostAtSign(value);
  return postAtSign.substring(0, getTldPeriodPosition(value));
};

export const validateEmailNoSpace: EmailValidator = {
  validator: (value) => !value.match(/[\s\r\n\t\b\f]/),
  getValidationMessage: (value) => (
    <div key={'unrecognized-email-format-' + value}>
      <span className="email-invalidated">[{value}]</span> Spaces not valid in email addresses.
    </div>
  ),
};

export const validateEmailLength: EmailValidator = {
  validator: (value) =>
    value.length > UserValidators.EMAIL_MIN_LENGTH &&
    value.length < UserValidators.EMAIL_MAX_LENGTH,
  getValidationMessage: (value) => (
    <div key={'email-length-incorrect-' + value}>
      <span className="email-invalidated">[{value}]</span> Email character length must be at least{' '}
      {UserValidators.EMAIL_MIN_LENGTH} and no more than {UserValidators.EMAIL_MAX_LENGTH}.
    </div>
  ),
};

export const validateCharBeforeAtSymbol: EmailValidator = {
  validator: (value) => getAtSignPosition(value) >= 1,
  getValidationMessage: (value) => (
    <div key={'one-char-before-the-@-' + value}>
      <span className="email-invalidated">[{value}]</span> Must have at least one @ and at least one
      character before the @.
    </div>
  ),
};

export const validateTldLen: EmailValidator = {
  validator: (value) => getNumCharsTld(value) >= 2,
  getValidationMessage: (value) => (
    <div key={'tld-must-be-two-chars-' + value}>
      <span className="email-invalidated">[{value}]</span> The ending portion of a domain name is
      called a top-level domain and must have at least two characters (Example: .eu or .com).
    </div>
  ),
};

export const validateDomainLen: EmailValidator = {
  validator: (value) => getDomain(value).length >= 1,
  getValidationMessage: (value) => (
    <div key={'domains-and-subdomains-must-have-at-least-one-char-' + value}>
      <span className="email-invalidated">[{value}]</span> Must be at least one character in the
      domain name and must also include a top-level domain such as .com or .jp (Example:
      my-email@.com is not valid).
    </div>
  ),
};

export const validateDomainNoPeriod: EmailValidator = {
  validator: (value) => {
    const domain = getDomain(value);
    return domain[0] !== '.';
  },
  getValidationMessage: (value) => (
    <div key={'cannot-begin-with-period-' + value}>
      <span className="email-invalidated">[{value}]</span>
      Domains cannot begin with a period (Example: my-email@.flowjo.com is not valid).
    </div>
  ),
};

export const validateNoPeriodsTouching: EmailValidator = {
  validator: (value) => {
    const postAtSign = getPostAtSign(value);
    return !/\.\./.test(postAtSign);
  },
  getValidationMessage: (value) => (
    <div key={'periods-cannot-touch-each-other-' + value}>
      <span className="email-invalidated">[{value}]</span> Periods to right of @ cannot touch each
      other.
    </div>
  ),
};

// ------------------------------------------------------------------------------------------------

// TODO: do we really need validation messages at this level of detail? If not, can retire these
// validators in favor of `utils/validators#validateEmail`

interface ComposedValidator {
  validator: (value: string) => boolean;
  getValidationMessage: (value: string) => string | React.ReactElement[];
}

// `Utils.composeValidators` types its params as validators over `unknown`; our
// email validators are the same shape but narrowed to `string`, so cast the
// argument list through, then narrow the result to the composed-validator shape.
const validator = Utils.composeValidators(
  ...([
    validateEmailNoSpace,
    validateEmailLength,
    validateCharBeforeAtSymbol,
    validateTldLen,
    validateDomainLen,
    validateDomainNoPeriod,
    validateNoPeriodsTouching,
  ] as unknown as Parameters<typeof Utils.composeValidators>),
) as ComposedValidator;

export const validateEmail = validator.getValidationMessage;
